import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LogIn, X } from 'lucide-react';
import { attendanceAPI } from '../../services/api';
import { dayHoursOf } from './attendanceStatus';
import { asClockTime, clinicMinutes, minutesOf, reminderStore, todaySlot } from './reminders';

// The first time somebody signs in on a working day, a nudge to mark their time in. Forgetting at
// the start of the day is worse than forgetting at the end: there is no record at all to correct,
// and the day is counted as absent.
//
// The whole decision is the server's. `mine().today.status` is NOT_IN only when it is today, the
// person was expected in, nobody has punched yet, and the day is not over, so a weekly off, a
// hospital holiday, approved leave, a day before they joined and a day already worked are all
// excluded without this component knowing what any of those are. After the shift's end the status
// turns ABSENT by itself and the nudge stops rather than nagging into the night.
//
// Not shown to an administrator (the owner, 2026-10-05), and NOT a poll: the day is read once and
// the clock runs in the browser from there. A background read through apiRequest would sign the
// user out of the whole app on any 401.

const EARLY_MINUTES = 30;
const SNOOZE_MINUTES = 10;
const TICK_MS = 30 * 1000;

const STORE = reminderStore('clinic_punchin');

const PunchInReminder = ({ authUser, onOpenAttendance }) => {
  const userId = authUser?.id;
  const skip = !userId || authUser?.role === 'ADMIN';
  const [day, setDay] = useState(null);   // { startsAt: 'HH:mm' | null, notIn: bool }
  const [showing, setShowing] = useState(false);
  const [snoozeLeft, setSnoozeLeft] = useState(true);
  const checking = useRef(false);

  // One read of where they stand, repeated only when they actually punch.
  const load = useCallback(async () => {
    if (skip) return;
    try {
      const mine = await attendanceAPI.mine();
      const today = todaySlot(dayHoursOf(mine?.schedule));
      setDay({ startsAt: today?.from || null, notIn: mine?.today?.status === 'NOT_IN' });
    } catch {
      setDay(null);
    }
  }, [skip]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const again = () => load();
    window.addEventListener('clinic:attendance-changed', again);
    return () => window.removeEventListener('clinic:attendance-changed', again);
  }, [load]);

  useEffect(() => {
    if (!day || !day.notIn || showing) return undefined;

    const tick = async () => {
      const saved = STORE.read(userId);
      if (saved.state === 'done') return;

      // Somebody who opens the app at home hours before an evening clinic is not asked to punch in
      // for it: half an hour before their start is early enough for anyone arriving early, and a
      // punch made at breakfast would be auto-closed at the day's end as a full shift.
      const startsAt = minutesOf(day.startsAt);
      const now = clinicMinutes();
      const due = saved.state === 'snoozed'
        ? now >= saved.until
        : startsAt == null || now >= startsAt - EARLY_MINUTES;
      if (!due || checking.current) return;

      // They may have punched in on their phone while this page sat open.
      checking.current = true;
      try {
        const mine = await attendanceAPI.mine();
        if (mine?.today?.status !== 'NOT_IN') {
          setDay((d) => (d ? { ...d, notIn: false } : d));
          return;
        }
      } catch {
        return;
      } finally {
        checking.current = false;
      }
      setSnoozeLeft(saved.state !== 'snoozed');
      setShowing(true);
    };

    tick();
    const timer = setInterval(tick, TICK_MS);
    return () => clearInterval(timer);
  }, [day, showing, userId]);

  if (skip || !showing || !day) return null;

  const dismiss = () => {
    STORE.write(userId, { state: 'done' });
    setShowing(false);
  };

  const snooze = () => {
    STORE.write(userId, { state: 'snoozed', until: clinicMinutes() + SNOOZE_MINUTES });
    setShowing(false);
  };

  const openIt = () => {
    STORE.write(userId, { state: 'done' });
    setShowing(false);
    if (onOpenAttendance) onOpenAttendance();
  };

  // z-40, one below dialogs: at z-50 this card covered the bottom of an open dialog on a phone,
  // such as the punch window's position line and its buttons (2026-10-21).
  return (
    <div
      role="alertdialog"
      aria-label="Time to mark your time in"
      className="fixed z-40 bottom-4 right-4 left-4 sm:left-auto sm:w-96 bg-white rounded-2xl shadow-2xl border border-emerald-300"
    >
      <div className="p-4 flex gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
          <LogIn className="w-5 h-5 text-emerald-700" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900">Mark your time in</p>
          <p className="text-sm text-gray-600 mt-0.5">
            {day.startsAt
              ? `Your day starts at ${asClockTime(day.startsAt)}. Mark your time in, or today is counted as absent.`
              : 'You have not marked your time in today. Mark it now, or today is counted as absent.'}
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              onClick={openIt}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium"
            >
              Mark my time in
            </button>
            {snoozeLeft && (
              <button
                type="button"
                onClick={snooze}
                className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Remind me in {SNOOZE_MINUTES} minutes
              </button>
            )}
          </div>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={dismiss}
          className="p-1.5 h-fit hover:bg-gray-100 rounded-lg flex-shrink-0"
        >
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>
    </div>
  );
};

export default PunchInReminder;
